import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PhonelinkEraseOutlinedIcon from "@mui/icons-material/PhonelinkEraseOutlined";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type ClickableStatState, ClickableStatTile } from "../UIToolkit/molecules/ClickableStatTile";
import type { TeamStatsResult } from "./useGetTeamStats";

export type StatFilterKey = "workedLastMonth" | "neverLoggedIn" | "recurringShifts" | "selfScheduling";
export type ActiveStatFilter = { key: StatFilterKey; state: "matching" | "not-matching" } | null;

type TeamStatsProps = {
	stats: TeamStatsResult;
	activeStatFilter: ActiveStatFilter;
	onCycleStatFilter: (key: StatFilterKey) => void;
	onClearStatFilter: () => void;
	/** True while the roster is still arriving — the figures render as skeletons. */
	loading: boolean;
	/** Clears every filter, the search and the segment. Drives the Total-staff tile. */
	onResetAll: () => void;
};

function resolveState(activeStatFilter: ActiveStatFilter, key: StatFilterKey): ClickableStatState {
	if (activeStatFilter?.key !== key) return "default";
	return activeStatFilter.state;
}

const ICON_SX = { fontSize: "18px" } as const;

/**
 * The segment tiles above the roster.
 *
 * Each figure is ABSOLUTE — the whole department, never the current filter. A tile is doing two jobs
 * at once, reporting a number and acting as a button, and those conflict the moment the number
 * follows the filter: click "Worked last month" and the 124 you clicked becomes 19, so there is no
 * stable thing to click back to and you cannot tell whether the tile is a figure or a control. Fixed
 * to the whole roster, a tile always means the same segment and the TABLE is what responds.
 */
export const TeamStats: FC<TeamStatsProps> = ({ stats, activeStatFilter, onCycleStatFilter, onClearStatFilter, loading, onResetAll }) => {
	const { t } = useTranslation();
	const filteringLabel = t("team.stat.filtering_table");
	const clearLabel = t("team.stat.clear_segment");

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(auto-fit, minmax(180px, 1fr))" },
				gap: 1.25,
				py: 0.5,
			}}
		>
			<ClickableStatTile
				label={t("team.stat.total_staff")}
				value={stats.totalStaff}
				icon={<PeopleAltOutlinedIcon sx={ICON_SX} />}
				// A plain reset, never a selected state: the default view already hides inactive and
				// agency staff without counting as a filter, so a tile reading "showing all" beside a
				// search bar reading "180 of 312" would be claiming something untrue.
				onClick={onResetAll}
				loading={loading}
				dataTestId="team-stat-total-staff"
			/>
			<ClickableStatTile
				label={t("team.stat.worked_last_month")}
				value={stats.workedLastMonthNurseIds.size}
				icon={<HistoryOutlinedIcon sx={ICON_SX} />}
				state={resolveState(activeStatFilter, "workedLastMonth")}
				stateLabel={filteringLabel}
				onClick={() => onCycleStatFilter("workedLastMonth")}
				onClear={onClearStatFilter}
				clearLabel={clearLabel}
				loading={loading}
				dataTestId="team-stat-worked-last-month"
			/>
			<ClickableStatTile
				label={t("team.stat.never_logged_in")}
				value={stats.neverLoggedInNurseIds.size}
				icon={<PhonelinkEraseOutlinedIcon sx={ICON_SX} />}
				state={resolveState(activeStatFilter, "neverLoggedIn")}
				stateLabel={filteringLabel}
				onClick={() => onCycleStatFilter("neverLoggedIn")}
				onClear={onClearStatFilter}
				clearLabel={clearLabel}
				loading={loading}
				dataTestId="team-stat-never-logged-in"
			/>
			<ClickableStatTile
				label={t("team.stat.recurring_shifts_staff")}
				value={stats.recurringShiftsNurseIds.size}
				icon={<AutorenewOutlinedIcon sx={ICON_SX} />}
				state={resolveState(activeStatFilter, "recurringShifts")}
				stateLabel={filteringLabel}
				onClick={() => onCycleStatFilter("recurringShifts")}
				onClear={onClearStatFilter}
				clearLabel={clearLabel}
				loading={loading}
				dataTestId="team-stat-recurring-shifts"
			/>
			{stats.selfSchedulingSubmissions !== undefined && (
				<ClickableStatTile
					label={t("team.stat.self_scheduling_submissions")}
					value={stats.selfSchedulingSubmittedNurseIds.size}
					icon={<EventAvailableOutlinedIcon sx={ICON_SX} />}
					state={resolveState(activeStatFilter, "selfScheduling")}
					stateLabel={filteringLabel}
					onClick={() => onCycleStatFilter("selfScheduling")}
					onClear={onClearStatFilter}
					clearLabel={clearLabel}
					loading={loading}
					dataTestId="team-stat-self-scheduling"
				/>
			)}
		</Box>
	);
};
